import { useCallback, useEffect, useRef, useState } from 'react';
import type { Map as CarteGL, MapMouseEvent } from 'maplibre-gl';
import {
  accrocher, aireNetteM2, annuler as annulerNoyau, diagnostiquer, distanceM, estDeposable,
  executer, figure, insererSommetSurCote, mesureLineaireM, natureDe, orthogonaliser,
  ouvrirSession, polyligne, refaire as refaireNoyau, semis, sommet, supprimerSommet,
  premierCorps, versGeoJSON, versGeoJSONNature, depuisGeoJSON,
  type Accroche, type Diagnostic, type Figure, type Nature, type Point, type Session,
} from '../../src/partage/edition-geometrie.js';
import { api, ErreurApi, type DemandeImmatriculation, type VerdictEmprise } from './api';
import { avancerAuGisement, type UniteAngle } from '../../src/partage/cogo-gisement.js';
import { paletteCarte, alpha } from './palette-carte.js';

/**
 * L'OUTIL DE TRACÉ — la main qui manquait au moteur.
 *
 * ⚠️⚠️ IL N'Y A PAS DE SECONDE CARTE, ET C'EST LA PREMIÈRE RÈGLE DE CE FICHIER.
 * Ce dépôt a déjà payé ce défaut : `carte.tsx` (MapLibre) et `assemblage.tsx`
 * (un `<canvas>` de 1 290 lignes) dessinaient tous deux les sections, chacun
 * avec son cadrage, son zoom et son attribution ODbL — dont l'une écrite en dur
 * et déjà fausse. Cet outil s'attache donc à l'instance MapLibre EXISTANTE,
 * remise par la prop `onCarte`. Il ajoute une source et cinq couches ; il n'en
 * crée aucune autre.
 *
 * ⚠️⚠️ L'ACCROCHAGE NE LIT PAS LES TUILES, ET C'EST LE POINT LE PLUS SUBTIL
 * DU FICHIER. `queryRenderedFeatures` rendrait la géométrie telle que la tuile
 * la porte — or `tuiles.ts` la produit par
 * `ST_AsMVTGeom(..., 4096, 64, true)` : le dernier argument DÉCOUPE la
 * géométrie au bord de la tuile. Les sommets ainsi rendus comprennent donc des
 * points de COUTURE, qui n'existent sur aucun terrain et dans aucune base.
 * S'accrocher dessus poserait une borne sur une frontière de tuile, à un
 * endroit qui change avec le zoom — un défaut que rien n'afficherait, et qui
 * ne se verrait qu'au dépôt, ou jamais. On demande donc les voisines à
 * `GET /parcelles?ouest=…` (`dansEnveloppe`), qui rend la géométrie de la base,
 * entière.
 *
 * ⚠️ CE FICHIER NE JUGE PAS LE RECOUVREMENT. Il le DEMANDE, pendant le tracé,
 * à `POST /parcelles/verifier-emprise` — la même garde que celle qui refusera
 * au dépôt. Recalculer « à peu près la même chose » dans l'écran produirait
 * deux verdicts qui divergent au premier ajustement de tolérance, et c'est
 * toujours celui qu'on ne teste pas qui reste faux.
 */

const SOURCE = 'trace';
const CALQUES = ['trace-remplissage', 'trace-contour', 'trace-cotes', 'trace-sommets', 'trace-accroche'] as const;

/** Rayon de préhension d'un sommet, en pixels écran. */
const PRISE_PX = 9;

/**
 * Tolérance d'accrochage, exprimée en PIXELS puis convertie en mètres au zoom
 * courant.
 *
 * ⚠️ UNE TOLÉRANCE EN MÈTRES FIXES SERAIT FAUSSE À L'ÉCRAN. À z19 dix mètres
 * couvrent la moitié de la vue : tout s'accrocherait à tout. À z13 ils valent
 * deux pixels : plus rien ne s'accrocherait. Le géomètre vise avec sa souris,
 * donc la tolérance se règle en pixels — et le noyau, lui, ne connaît que les
 * mètres, parce que c'est la seule unité dans laquelle un accrochage a un sens
 * géodésique. La conversion se fait ici, à un seul endroit.
 */
const ACCROCHE_PX = 12;

export type ModeTrace = 'inactif' | 'tracer' | 'modifier';

export interface EtatTrace {
  mode: ModeTrace;
  /** Ce qu'on trace : borne, voie, ou emprise. */
  nature: Nature;
  sommets: number;
  aireM2: number;
  /** Longueur pour une ligne, périmètre pour une surface — jamais l'un pour l'autre. */
  mesureLineaireM: number;
  diagnostics: Diagnostic[];
  deposable: boolean;
  peutAnnuler: boolean;
  peutRefaire: boolean;
  /** Le dernier accrochage effectif — pour que l'écran le NOMME. */
  accroche: Accroche | null;
  verdict: VerdictEmprise | null;
  verdictEnCours: boolean;
  erreur: string | null;
  voisinesChargees: number;
  /** Dépôt en cours — le bouton doit s'éteindre, sinon on dépose deux fois. */
  depotEnCours: boolean;
  /**
   * LA PARCELLE QUE CETTE SESSION CORRIGE — `null` quand on trace du neuf.
   *
   * ⚠️⚠️ `corps` N'EST PAS DÉCORATIF. L'éditeur ne reprend que le PREMIER corps
   * d'une géométrie multiple. Tant que le dépôt créait une parcelle neuve,
   * c'était un avertissement ; dès qu'il CORRIGE, réécrire `geom` avec le seul
   * premier corps EFFACE les autres. Le dépôt s'y refuse, et l'écran le dit
   * avant que l'agent ne dessine pendant vingt minutes.
   */
  corrige: { identifiant: string; corps: number } | null;
  /** Ce que le registre a inscrit, une fois le dépôt abouti. */
  depose: {
    identifiant: string; statut: string;
    /** Créé, corrigé — ou PROPOSÉ : trois phrases, trois conséquences. Une
        proposition n'inscrit RIEN au registre : elle attend un tiers. */
    acte: 'creation' | 'correction' | 'proposition' | 'acte_depose';
    /** L'écart de surface d'une correction : une erreur de saisie se voit là. */
    ecartSurfacePct: number | null;
  } | null;
}

export interface GestesTrace {
  demarrer: (nature: Nature) => void;
  reprendre: (g: unknown, identifiant: string) => void;
  arreter: () => void;
  annuler: () => void;
  refaire: () => void;
  orthogonaliser: () => void;
  vider: () => void;
  geometrie: () => unknown | null;
  /**
   * POSER LE PROCHAIN SOMMET AU CLAVIER — distance + gisement depuis le
   * DERNIER sommet (D.946). Rend le motif du refus, ou `null` si posé.
   * Le point passe par le MÊME accrochage que la souris : saisi ou cliqué,
   * un point visé sur un coin existant écrit LE point de ce coin.
   */
  poserAuGisement: (gisement: number, unite: UniteAngle, distanceM: number) => string | null;
  deposer: (complements: Omit<DemandeImmatriculation, 'geometrie'>) => Promise<void>;
}

/** Conversion pixels → mètres au centre de la vue courante. */
function metresParPixel(c: CarteGL): number {
  const centre = c.getCenter();
  const a = c.unproject([0, 0]);
  const b = c.unproject([1, 0]);
  const d = distanceM({ lon: a.lng, lat: a.lat }, { lon: b.lng, lat: b.lat });
  // Un garde-fou : au tout premier rendu la toile peut mesurer 0 px.
  return Number.isFinite(d) && d > 0 ? d : 156543.03 * Math.cos((centre.lat * Math.PI) / 180) / 2 ** c.getZoom();
}

/**
 * L'écrivain DIRECT de la reprise d'emprise — l'unique implémentation, employée
 * par le moteur quand aucun écrivain n'est injecté ET par l'atelier pour la
 * branche « projet » de son aiguillage par statut (D.1499).
 */
export async function correctionDirecte(identifiant: string, corps: {
  geometrie: unknown; origine: DemandeImmatriculation['origine'];
  precisionM?: number; methode?: string; source?: string; motif: string;
}): Promise<{ identifiant: string; statut: string; acte: 'correction'; ecartSurfacePct: number | null }> {
  const r = await api.parcelles.modifierEmprise(identifiant, corps as any);
  return { identifiant: r.parcelle, statut: r.statut, acte: 'correction', ecartSurfacePct: r.ecart_surface_pct };
}

export function useTrace(
  carte: CarteGL | null,
  /**
   * Appelé quand un dépôt a ABOUTI — c'est le signal qui fait redemander la
   * version du plan, donc changer l'URL des tuiles, donc repeindre la carte.
   *
   * ⚠️ IL NE SE DÉCLENCHE PAS SUR UN ÉCHEC. Rafraîchir après un refus ferait
   * croire à l'agent que quelque chose a été inscrit — le pire contresens
   * possible : il chercherait ensuite sa parcelle sur un plan qui ne l'a
   * jamais reçue.
   */
  onDepose?: () => void,
  /**
   * L'ÉCRIVAIN de la reprise d'emprise — la fourche par MÉTIER (D.1488).
   * Par défaut : l'application directe au registre (cad.parcelle:modifier).
   * ⚠️ Ne pas citer ici le nom complet de l'appel : le banc de l'ordre
   * (correction-emprise) vérifie par indexOf sur le source BRUT que la
   * PREMIÈRE occurrence vient après la lecture de `correction.current`. L'atelier d'un GÉOMÈTRE injecte ici le dépôt d'un
   * PROJET DE LEVÉ : même moteur de tracé, même accrochage, un autre acte —
   * et l'acte rendu (`proposition`) fait écrire une autre phrase à la barre.
   * Deux implémentations RÉELLES existent : c'est ce qui justifie le paramètre.
   */
  deposerCorrection?: (identifiant: string, corps: {
    geometrie: unknown; origine: DemandeImmatriculation['origine'];
    precisionM?: number; methode?: string; source?: string; motif: string;
  }) => Promise<{ identifiant: string; statut: string; acte: 'correction' | 'proposition' | 'acte_depose'; ecartSurfacePct: number | null }>,
): [EtatTrace, GestesTrace] {
  const session = useRef<Session | null>(null);
  const voisines = useRef<Figure[]>([]);
  /** Le dernier accrochage, pour le PEINDRE — item D.946 : un accrochage dit
   *  en toutes lettres mais invisible sur le plan laisse l'agent viser à
   *  l'aveugle l'endroit exact où la précision compte. */
  const accrochePeinte = useRef<Accroche | null>(null);
  const mode = useRef<ModeTrace>('inactif');
  const nature = useRef<Nature>('surface');
  const sommetTire = useRef<{ anneauId: string; sommetId: string } | null>(null);
  /**
   * La position du sommet À L'INSTANT DE L'APPUI — ce qui rend le tirage
   * annulable d'un seul geste.
   *
   * ⚠️ UN `ref` DU COMPOSANT, PAS UNE VARIABLE DE L'EFFET. Une première
   * rédaction le déclarait dans l'effet, APRÈS les fonctions qui le lisent :
   * licite par hissage, illisible à la relecture, et remis à zéro à chaque
   * réexécution de l'effet — donc un tirage en cours au moment où le parent
   * rerend aurait perdu son origine, et le déplacement n'aurait plus été
   * annulable, sans erreur.
   */
  const origineTirage = useRef<Point | null>(null);
  /**
   * ⚠️ LE CLAVIER APPELLE DES GESTES DÉFINIS PLUS BAS DANS CE FICHIER. Les
   * capturer directement figerait la version du premier rendu — l'effet ne se
   * rejoue qu'au changement de `carte`. Un `ref` remis à chaque rendu donne
   * toujours la version courante.
   */
  /**
   * CE QUE LA SESSION CORRIGE — un `ref`, pour la même raison que `mode` et
   * `nature` : `deposer()` est lu depuis une fermeture qui ne se rejoue pas,
   * et un état y resterait figé à sa valeur de montage.
   */
  const correction = useRef<{ identifiant: string; corps: number } | null>(null);
  const gestesRef = useRef<GestesTrace | null>(null);
  const minuterie = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [etat, setEtat] = useState<EtatTrace>({
    mode: 'inactif', nature: 'surface', sommets: 0, aireM2: 0, mesureLineaireM: 0, diagnostics: [],
    deposable: false, peutAnnuler: false, peutRefaire: false, accroche: null,
    verdict: null, verdictEnCours: false, erreur: null, voisinesChargees: 0,
    depotEnCours: false, depose: null, corrige: null,
  });

  // ── Peindre ───────────────────────────────────────────────────────────────

  const peindre = useCallback(() => {
    const c = carte;
    const s = session.current;
    if (!c || !c.getSource(SOURCE)) return;
    const src = c.getSource(SOURCE) as maplibregl.GeoJSONSource;

    const traits: GeoJSON.Feature[] = [];
    if (s) {
      /**
       * ⚠️ LA NATURE COMMANDE LE DESSIN, ET PAS SEULEMENT L'ALLURE. Une voie
       * peinte avec la fermeture du polygone se refermerait en boucle à
       * l'écran : l'agent verrait une figure qu'il n'a pas tracée, et le
       * fichier déposé, lui, serait juste. Deux vérités, dont la fausse est
       * celle qu'il regarde.
       */
      const nat = natureDe(s.figure);
      const ferme = nat === 'surface';
      for (const an of s.figure.anneaux) {
        const pts = an.sommets.map((v) => [v.lon, v.lat]);
        /**
         * ⚠️ LE POLYGONE N'APPARAÎT QU'À TROIS SOMMETS, LA LIGNE DÈS DEUX.
         * MapLibre accepte un anneau de deux points sans broncher et n'en peint
         * rien : l'agent poserait deux sommets et verrait une carte vide, sans
         * savoir si son clic a porté. La ligne, elle, se voit dès le premier
         * segment.
         */
        if (ferme && pts.length >= 3) {
          traits.push({
            type: 'Feature', properties: { r: 'surface' },
            geometry: { type: 'Polygon', coordinates: [[...pts, pts[0]!]] as number[][][] },
          });
        }
        // Un semis n'a pas de contour : relier des bornes dessinerait une
        // limite que personne n'a levée.
        if (nat !== 'point' && pts.length >= 2) {
          traits.push({
            type: 'Feature', properties: { r: 'contour' },
            geometry: { type: 'LineString', coordinates: ferme && pts.length >= 3 ? [...pts, pts[0]!] : pts },
          });
        }
        for (const v of an.sommets) {
          traits.push({
            type: 'Feature', properties: { r: 'sommet', id: v.id, anneau: an.id },
            geometry: { type: 'Point', coordinates: [v.lon, v.lat] },
          });
        }
        // Les milieux de côté : la poignée qui INSÈRE. Sans elle, ajouter un
        // sommet au milieu d'un côté existant n'a aucun geste. Un semis n'en a
        // pas : il n'y a pas de « milieu » entre deux bornes indépendantes.
        if (nat !== 'point' && pts.length >= 2) {
          const n = an.sommets.length;
          const dernier = ferme ? n : n - 1;
          for (let i = 0; i < dernier; i += 1) {
            const a = an.sommets[i];
            const b = an.sommets[(i + 1) % n];
            if (!a || !b) continue;
            traits.push({
              type: 'Feature', properties: { r: 'cote' },
              geometry: { type: 'Point', coordinates: [(a.lon + b.lon) / 2, (a.lat + b.lat) / 2] },
            });
          }
        }
      }
    }
    /* ⚠️ LE TÉMOIN VISUEL D'ACCROCHAGE — peint, pas seulement écrit (D.946). */
    if (accrochePeinte.current) {
      traits.push({
        type: 'Feature', properties: { r: 'accroche' },
        geometry: {
          type: 'Point',
          coordinates: [accrochePeinte.current.point.lon, accrochePeinte.current.point.lat],
        },
      });
    }
    src.setData({ type: 'FeatureCollection', features: traits });
  }, [carte]);

  // ── Publier l'état vers l'écran ───────────────────────────────────────────

  const publier = useCallback((suite?: Partial<EtatTrace>) => {
    const s = session.current;
    const an = s?.figure.anneaux[0];
    setEtat((prec) => ({
      ...prec,
      mode: mode.current,
      nature: nature.current,
      sommets: an?.sommets.length ?? 0,
      aireM2: s ? aireNetteM2(s.figure) : 0,
      mesureLineaireM: s ? mesureLineaireM(s.figure) : 0,
      diagnostics: s ? diagnostiquer(s.figure) : [],
      deposable: s ? estDeposable(s.figure) : false,
      peutAnnuler: (s?.passe.length ?? 0) > 0,
      peutRefaire: (s?.futur.length ?? 0) > 0,
      ...suite,
    }));
    peindre();
  }, [peindre]);

  // ── Demander le verdict au serveur, sans le harceler ──────────────────────

  const demanderVerdict = useCallback(() => {
    if (minuterie.current) clearTimeout(minuterie.current);
    const s = session.current;
    /**
     * ⚠️ LE CONTRÔLE D'EMPRISE NE CONCERNE QUE LES SURFACES. `versGeoJSON`
     * rend un `Polygon` quoi qu'on lui donne : interrogée avec une voie, la
     * garde comparerait le polygone obtenu en la REFERMANT à des parcelles
     * voisines, et rendrait un verdict de recouvrement sur une figure qui
     * n'existe pas. Un verdict faux est pire qu'aucun verdict.
     */
    if (!s || natureDe(s.figure) !== 'surface' || !estDeposable(s.figure)) {
      setEtat((p) => ({ ...p, verdict: null, verdictEnCours: false }));
      return;
    }
    /**
     * ⚠️ 400 ms, ET LA MINUTERIE EST REMISE À CHAQUE GESTE. Sans cela, tirer un
     * sommet déclencherait une requête par image — soit soixante par seconde,
     * chacune ouvrant une comparaison spatiale sur l'index GIST. Ce n'est pas
     * une optimisation de confort : c'est ce qui empêche un agent qui déplace
     * un point de mettre le registre à genoux pour tout le monde.
     */
    setEtat((p) => ({ ...p, verdictEnCours: true }));
    minuterie.current = setTimeout(async () => {
      const courante = session.current;
      if (!courante) return;
      try {
        const v = await api.parcelles.verifierEmprise(versGeoJSON(courante.figure));
        setEtat((p) => ({ ...p, verdict: v, verdictEnCours: false, erreur: null }));
      } catch (e) {
        setEtat((p) => ({
          ...p, verdict: null, verdictEnCours: false,
          erreur: e instanceof ErreurApi ? e.message : "Le contrôle d'emprise n'a pas répondu.",
        }));
      }
    }, 400);
  }, []);

  const apresGeste = useCallback(() => { publier(); demanderVerdict(); }, [publier, demanderVerdict]);

  // ── Les voisines, depuis la BASE et non depuis les tuiles ─────────────────

  const chargerVoisines = useCallback(async () => {
    const c = carte;
    if (!c || mode.current === 'inactif') return;
    const b = c.getBounds();
    try {
      const col = await api.parcelles.dansEnveloppe(
        { ouest: b.getWest(), sud: b.getSouth(), est: b.getEast(), nord: b.getNorth() }, 400);
      const figures: Figure[] = [];
      for (const f of (col as any)?.features ?? []) {
        const g = f?.geometry;
        if (!g) continue;
        // `dansEnveloppe` rend des Polygon ET des MultiPolygon : ne traiter que
        // le premier cas laisserait sans accrochage toutes les parcelles à
        // plusieurs corps, sans rien dire.
        const polys: number[][][][] = g.type === 'MultiPolygon' ? g.coordinates
          : g.type === 'Polygon' ? [g.coordinates] : [];
        for (const p of polys) figures.push(depuisGeoJSON({ type: 'Polygon', coordinates: p }));
      }
      voisines.current = figures;
      setEtat((p) => ({ ...p, voisinesChargees: figures.length }));
    } catch {
      /* ⚠️ UN ÉCHEC DE CHARGEMENT NE DOIT PAS SE LIRE « AUCUNE VOISINE ». On
         garde les précédentes et l'écran affiche le compte : un 0 annoncé est
         un 0 mesuré, jamais un silence. */
    }
  }, [carte]);

  /**
   * ⚠️ UNE SEULE RÈGLE D'ACCROCHAGE POUR TOUS LES GESTES — souris ET clavier
   * (D.946). La tolérance vivait dans la fermeture du montage, inaccessible à
   * `poserAuGisement` : la réécrire là-bas aurait fait DEUX formules qui
   * divergent au premier réglage. C'est l'égalité que le banc éprouve.
   */
  const optionsAccroche = useCallback(() => ({
    toleranceM: ACCROCHE_PX * metresParPixel(carte!),
    voisines: voisines.current,
  }), [carte]);

  /* Un seul site de synchronisation : l'accrochage de l'état est celui qui
     se peint. Le poser dans chaque gestionnaire de geste en ferait oublier un. */
  useEffect(() => {
    accrochePeinte.current = etat.accroche;
    peindre();
  }, [etat.accroche, peindre]);

  // ── Monter la source et les couches ───────────────────────────────────────

  useEffect(() => {
    const c = carte;
    if (!c) return;
    let vivant = true;

    const monter = () => {
      if (!vivant || c.getSource(SOURCE)) return;
      const P = paletteCarte();
      const teinte = P.choix;
      c.addSource(SOURCE, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });

      c.addLayer({
        id: 'trace-remplissage', type: 'fill', source: SOURCE,
        filter: ['==', ['get', 'r'], 'surface'],
        paint: { 'fill-color': alpha(teinte, 0.18) },
      });
      c.addLayer({
        id: 'trace-contour', type: 'line', source: SOURCE,
        filter: ['==', ['get', 'r'], 'contour'],
        paint: { 'line-color': teinte, 'line-width': 2 },
      });
      c.addLayer({
        id: 'trace-cotes', type: 'circle', source: SOURCE,
        filter: ['==', ['get', 'r'], 'cote'],
        paint: {
          'circle-radius': 3.5, 'circle-color': P.fond,
          'circle-stroke-color': teinte, 'circle-stroke-width': 1.5, 'circle-opacity': 0.9,
        },
      });
      c.addLayer({
        id: 'trace-sommets', type: 'circle', source: SOURCE,
        filter: ['==', ['get', 'r'], 'sommet'],
        paint: {
          'circle-radius': 5.5, 'circle-color': teinte,
          'circle-stroke-color': P.fond, 'circle-stroke-width': 1.5,
        },
      });
      /* L'anneau d'accrochage — creux, par-dessus tout : il marque un point
         qui appartient déjà à quelqu'un d'autre. */
      c.addLayer({
        id: 'trace-accroche', type: 'circle', source: SOURCE,
        filter: ['==', ['get', 'r'], 'accroche'],
        paint: {
          'circle-radius': 9, 'circle-color': 'transparent',
          'circle-stroke-color': teinte, 'circle-stroke-width': 2,
        },
      });
      peindre();
    };

    /**
     * ⚠️ ATTENDRE LE STYLE, SANS QUOI L'AJOUT ÉCHOUE — ET ÉCHOUE EN SILENCE.
     * `onCarte` est appelée au montage de la carte, pas à la fin du chargement
     * de son style. `addLayer` sur un style non chargé lève, ou pire ne peint
     * rien : l'outil paraîtrait alors « ne pas marcher » alors qu'il n'a
     * simplement jamais été monté.
     */
    if (c.isStyleLoaded()) monter();
    else c.once('load', monter);

    return () => {
      vivant = false;
      if (minuterie.current) clearTimeout(minuterie.current);
      // L'ordre compte : les couches d'abord, la source ensuite.
      for (const id of CALQUES) if (c.getLayer(id)) c.removeLayer(id);
      if (c.getSource(SOURCE)) c.removeSource(SOURCE);
    };
  }, [carte, peindre]);

  // ── Les gestes à la souris ────────────────────────────────────────────────

  useEffect(() => {
    const c = carte;
    if (!c) return;

    /** Le sommet sous le curseur, s'il y en a un — en pixels, pas en mètres. */
    const sommetSous = (e: MapMouseEvent): { anneauId: string; sommetId: string } | null => {
      const s = session.current;
      if (!s) return null;
      for (const an of s.figure.anneaux) {
        for (const v of an.sommets) {
          const p = c.project([v.lon, v.lat]);
          if (Math.hypot(p.x - e.point.x, p.y - e.point.y) <= PRISE_PX) {
            return { anneauId: an.id, sommetId: v.id };
          }
        }
      }
      return null;
    };

    const surClic = (e: MapMouseEvent) => {
      const s = session.current;
      if (!s || mode.current === 'inactif') return;

      // Un clic sur un milieu de côté INSÈRE ; c'est le seul geste d'ajout au
      // milieu d'un contour déjà fermé.
      const surCote = c.queryRenderedFeatures(e.point, { layers: ['trace-cotes'] });
      if (surCote.length > 0) {
        insererSommetSurCote(s, { lon: e.lngLat.lng, lat: e.lngLat.lat });
        apresGeste();
        return;
      }
      if (sommetSous(e)) return;   // un clic sur un sommet n'en crée pas un second

      const brut = { lon: e.lngLat.lng, lat: e.lngLat.lat };
      const acc = accrocher(brut, s.figure, optionsAccroche());
      const an = s.figure.anneaux[0];
      if (!an) return;
      executer(s, {
        type: 'inserer', anneauId: an.id, index: an.sommets.length,
        sommet: sommet(acc?.point.lon ?? brut.lon, acc?.point.lat ?? brut.lat),
      });
      setEtat((p) => ({ ...p, accroche: acc }));
      apresGeste();
    };

    const surAppui = (e: MapMouseEvent) => {
      if (mode.current === 'inactif') return;
      const cible = sommetSous(e);
      if (!cible) return;
      /**
       * ⚠️ ALT + CLIC SUPPRIME, ET LE REFUS S'AFFICHE. Le noyau refuse de
       * descendre sous trois sommets ; si l'écran avalait ce refus, l'agent
       * recliquerait en croyant que le geste n'a pas porté.
       */
      if (e.originalEvent.altKey) {
        e.preventDefault();
        const r = supprimerSommet(session.current!, cible.anneauId, cible.sommetId);
        if (!r.fait) setEtat((p) => ({ ...p, erreur: r.motif ?? null }));
        else setEtat((p) => ({ ...p, erreur: null }));
        apresGeste();
        return;
      }
      // Tirer un sommet : on coupe le déplacement de la carte, sinon les deux
      // gestes se superposent et la vue part avec le point.
      e.preventDefault();
      sommetTire.current = cible;
      c.dragPan.disable();
      c.getCanvas().style.cursor = 'grabbing';
    };

    const surMouvement = (e: MapMouseEvent) => {
      const s = session.current;
      const tire = sommetTire.current;
      if (!s || mode.current === 'inactif') return;

      if (!tire) {
        // Curseur : dire ce qui est préhensible AVANT qu'on essaie.
        c.getCanvas().style.cursor = sommetSous(e) ? 'grab' : (mode.current === 'tracer' ? 'crosshair' : '');
        return;
      }
      const brut = { lon: e.lngLat.lng, lat: e.lngLat.lat };
      const acc = accrocher(brut, s.figure, { ...optionsAccroche(), ignorer: [tire.sommetId] });
      const cible = acc?.point ?? brut;
      const an = s.figure.anneaux.find((x) => x.id === tire.anneauId);
      const v = an?.sommets.find((x) => x.id === tire.sommetId);
      if (!an || !v) return;
      /**
       * ⚠️ ON ÉCRIT LE SOMMET DIRECTEMENT PENDANT LE TIRAGE, ET L'HISTORIQUE NE
       * REÇOIT QU'UNE SEULE COMMANDE AU RELÂCHEMENT. Empiler une commande par
       * image ferait qu'annuler un déplacement demanderait soixante
       * annulations — l'agent appuierait sur « annuler » et verrait le point
       * reculer d'un pixel.
       */
      v.lon = cible.lon;
      v.lat = cible.lat;
      setEtat((p) => ({ ...p, accroche: acc }));
      publier();
    };

    const surRelachement = () => {
      const tire = sommetTire.current;
      if (!tire) return;
      const s = session.current;
      sommetTire.current = null;
      c.dragPan.enable();
      c.getCanvas().style.cursor = '';
      if (!s) return;
      const an = s.figure.anneaux.find((x) => x.id === tire.anneauId);
      const v = an?.sommets.find((x) => x.id === tire.sommetId);
      if (!an || !v) return;
      /**
       * La commande est posée MAINTENANT, avec l'origine retenue à l'appui.
       * Elle est donc annulable d'un seul geste, et le `futur` est vidé comme
       * pour n'importe quelle autre.
       */
      const depart = origineTirage.current;
      if (depart) {
        s.passe.push({
          type: 'deplacer', anneauId: an.id, sommetId: v.id,
          avant: depart, apres: { lon: v.lon, lat: v.lat },
        });
        s.futur.length = 0;
        origineTirage.current = null;
      }
      apresGeste();
    };

    const surAppuiAvecOrigine = (e: MapMouseEvent) => {
      const cible = sommetSous(e);
      if (cible && !e.originalEvent.altKey && mode.current !== 'inactif') {
        const s = session.current;
        const an = s?.figure.anneaux.find((x) => x.id === cible.anneauId);
        const v = an?.sommets.find((x) => x.id === cible.sommetId);
        if (v) origineTirage.current = { lon: v.lon, lat: v.lat };
      }
      surAppui(e);
    };

    c.on('click', surClic);
    c.on('mousedown', surAppuiAvecOrigine);
    c.on('mousemove', surMouvement);
    c.on('mouseup', surRelachement);
    c.on('moveend', chargerVoisines);

    return () => {
      c.off('click', surClic);
      c.off('mousedown', surAppuiAvecOrigine);
      c.off('mousemove', surMouvement);
      c.off('mouseup', surRelachement);
      c.off('moveend', chargerVoisines);
      c.dragPan.enable();
    };
  }, [carte, apresGeste, publier, chargerVoisines]);

  // ── Le clavier ────────────────────────────────────────────────────────────

  /**
   * ⚠️⚠️ SANS CLAVIER, UN ÉDITEUR N'EST PAS UN ÉDITEUR. Mesuré le 23/09/2026 :
   * `keydown` ne figurait NULLE PART dans cet écran — tout passait par la
   * souris, `Alt`+clic compris. Un agent qui veut annuler doit alors viser un
   * bouton dans une barre, en gardant l'autre main sur le tracé : c'est ce qui
   * fait abandonner un outil au profit du papier.
   *
   * ⚠️ `metaKey` AUTANT QUE `ctrlKey`. Un poste sous macOS n'envoie jamais
   * `ctrlKey` sur ⌘Z ; ne lire que `ctrlKey` rend le raccourci inerte sur la
   * moitié des postes, en silence, et sans qu'aucun essai ne le voie.
   *
   * ⚠️ ET ON NE VOLE PAS LES TOUCHES D'UN CHAMP DE SAISIE. Le formulaire de
   * dépôt est dans la même page : intercepter ⌘Z pendant que l'agent corrige
   * son motif annulerait un sommet au lieu de sa frappe — un geste dont l'effet
   * se produit ailleurs que là où il regarde.
   */
  useEffect(() => {
    if (!carte) return;
    const dansUneSaisie = (c: EventTarget | null): boolean => {
      const e = c as HTMLElement | null;
      if (!e || !e.tagName) return false;
      return ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.tagName) || e.isContentEditable === true;
    };

    const surTouche = (ev: KeyboardEvent) => {
      if (mode.current === 'inactif' || dansUneSaisie(ev.target)) return;
      const cmd = ev.ctrlKey || ev.metaKey;

      if (ev.key === 'Escape') {
        ev.preventDefault();
        /* Échap pendant un tirage RELÂCHE le point sans fermer l'outil : fermer
           d'un coup ferait perdre tout le tracé pour un geste qui voulait
           seulement interrompre un glissement. */
        if (sommetTire.current) {
          sommetTire.current = null;
          origineTirage.current = null;
          carte.dragPan.enable();
          carte.getCanvas().style.cursor = '';
          publier();
          return;
        }
        gestesRef.current?.arreter();
        return;
      }

      if (cmd && (ev.key === 'z' || ev.key === 'Z')) {
        ev.preventDefault();
        if (ev.shiftKey) gestesRef.current?.refaire();
        else gestesRef.current?.annuler();
        return;
      }
      if (cmd && (ev.key === 'y' || ev.key === 'Y')) {
        ev.preventDefault();
        gestesRef.current?.refaire();
        return;
      }

      if (ev.key === 'Delete' || ev.key === 'Backspace') {
        /* ⚠️ RETIRE LE DERNIER SOMMET POSÉ, PAS UN SOMMET AU HASARD. Pendant un
           tracé, c'est le geste attendu — défaire le clic de trop. Le noyau
           refuse en dessous de trois sommets pour une surface, et le dit. */
        const s = session.current;
        const an = s?.figure.anneaux[0];
        if (!s || !an || an.sommets.length === 0) return;
        ev.preventDefault();
        const dernier = an.sommets[an.sommets.length - 1];
        if (!dernier) return;
        const r = supprimerSommet(s, an.id, dernier.id);
        if (!r.fait) {
          /* Un semis ou une ligne peuvent descendre plus bas qu'une surface :
             on retire alors la commande à la main, par la même voie annulable. */
          if (natureDe(s.figure) !== 'surface') {
            executer(s, {
              type: 'supprimer', anneauId: an.id,
              index: an.sommets.length - 1, sommet: { ...dernier },
            });
          } else {
            setEtat((p) => ({ ...p, erreur: r.motif ?? null }));
          }
        }
        apresGeste();
      }
    };

    window.addEventListener('keydown', surTouche);
    return () => window.removeEventListener('keydown', surTouche);
  }, [carte, publier, apresGeste]);

  // ── Les gestes offerts à l'écran ──────────────────────────────────────────

  const gestes: GestesTrace = {
    demarrer: (n: Nature) => {
      nature.current = n;
      session.current = ouvrirSession(
        n === 'point' ? semis([]) : n === 'ligne' ? polyligne([]) : figure([]));
      mode.current = 'tracer';
      // `depose` est remis à zéro : laisser l'ancien accusé afficherait la
      // parcelle PRÉCÉDENTE au-dessus d'un tracé neuf.
      correction.current = null;
      setEtat((p) => ({
        ...p, erreur: null, verdict: null, accroche: null, depose: null, corrige: null,
      }));
      publier();
      void chargerVoisines();
    },
    reprendre: (g: unknown, identifiant: string) => {
      try {
        /* La lecture vit dans le noyau et s'y éprouve : `premierCorps` est
           partagé avec le serveur, qui rejoue le même code. */
        const lu = premierCorps(g);
        if (!lu) throw new Error('géométrie illisible');
        /**
         * ⚠️⚠️ LE MESSAGE A CHANGÉ DE NATURE AVEC LE DÉPÔT. Il disait « ne
         * déposez pas sans en tenir compte » — juste tant que le dépôt créait
         * une parcelle neuve à côté. Maintenant qu'il CORRIGE celle-ci,
         * réécrire `geom` avec le premier corps seul en efface les autres,
         * définitivement et sans erreur. On ne prévient plus : on refuse, et
         * `deposer()` refuse aussi, parce qu'un raccourci clavier n'a pas à
         * rencontrer une autre règle que le bouton.
         */
        const avertissement = lu.corps > 1
          ? `Cette parcelle porte ${lu.corps} corps séparés et l'éditeur n'en reprend que le `
            + `premier : la corriger ici effacerait les ${lu.corps - 1} autres. `
            + `Passez par un levé.`
          : null;
        session.current = ouvrirSession(depuisGeoJSON(lu.polygone));
        nature.current = natureDe(session.current.figure);
        mode.current = 'modifier';
        correction.current = { identifiant, corps: lu.corps };
        setEtat((p) => ({
          ...p, erreur: avertissement, depose: null,
          corrige: { identifiant, corps: lu.corps },
        }));
        publier();
        void chargerVoisines();
        demanderVerdict();
      } catch {
        setEtat((p) => ({ ...p, erreur: 'Cette géométrie n\'est pas lisible comme une emprise.' }));
      }
    },
    arreter: () => {
      session.current = null;
      mode.current = 'inactif';
      sommetTire.current = null;
      correction.current = null;
      setEtat((p) => ({
        ...p, mode: 'inactif', accroche: null, verdict: null, erreur: null, corrige: null,
      }));
      publier();
    },
    annuler: () => { if (session.current) { annulerNoyau(session.current); apresGeste(); } },
    refaire: () => { if (session.current) { refaireNoyau(session.current); apresGeste(); } },
    orthogonaliser: () => {
      const s = session.current;
      const an = s?.figure.anneaux[0];
      if (!s || !an) return;
      const n = orthogonaliser(s, an.id);
      /* ⚠️ ZÉRO CÔTÉ REDRESSÉ EST UN RÉSULTAT, PAS UNE PANNE. Sur un contour
         dont aucun angle n'approche 90°, l'outil ne touche à rien — et doit le
         DIRE, sinon l'agent reclique en croyant au bouton mort. */
      setEtat((p) => ({
        ...p,
        erreur: n === 0 ? "Aucun côté n'était assez proche de l'angle droit : rien n'a été redressé." : null,
      }));
      apresGeste();
    },
    vider: () => {
      if (!session.current) return;
      const n = nature.current;
      session.current = ouvrirSession(
        n === 'point' ? semis([]) : n === 'ligne' ? polyligne([]) : figure([]));
      setEtat((p) => ({ ...p, verdict: null, accroche: null, erreur: null }));
      publier();
    },
    geometrie: () => (session.current ? versGeoJSONNature(session.current.figure) : null),

    poserAuGisement: (gisement, unite, distanceM) => {
      const s = session.current;
      const c = carte;
      if (!s || !c || mode.current === 'inactif') return 'Aucun tracé en cours.';
      const an = s.figure.anneaux[0];
      const dernier = an?.sommets[an.sommets.length - 1];
      if (!an || !dernier) {
        return 'Posez d’abord le premier point à la souris : le gisement part du dernier sommet.';
      }
      if (!Number.isFinite(distanceM) || distanceM <= 0 || !Number.isFinite(gisement)) {
        return 'Distance strictement positive et gisement numériques.';
      }
      const brut = avancerAuGisement(dernier.lon, dernier.lat, gisement, unite, distanceM);
      /* ⚠️ LE MÊME ACCROCHAGE QUE LE CLIC — même tolérance, mêmes voisines.
         Deux chemins de pose avec deux règles d'accrochage divergeraient au
         premier réglage ; c'est l'égalité que le banc D.946 éprouve. */
      const acc = accrocher(brut, s.figure, optionsAccroche());
      executer(s, {
        type: 'inserer', anneauId: an.id, index: an.sommets.length,
        sommet: sommet(acc?.point.lon ?? brut.lon, acc?.point.lat ?? brut.lat),
      });
      setEtat((p) => ({ ...p, accroche: acc, erreur: null }));
      apresGeste();
      return null;
    },
    deposer: async (complements) => {
      const s = session.current;
      if (!s) return;
      /**
       * ⚠️ LE NOYAU RETIENT LA FIGURE AVANT L'ENVOI, ET CE N'EST PAS REDONDANT
       * AVEC LE SERVEUR. Mesuré en écrivant `tests/trace-contrat-serveur.test.ts` :
       * `geometrieSurfacique` n'exige qu'UN sommet par anneau — une figure à
       * deux sommets franchit donc la validation de forme et ne tombe qu'à
       * PostGIS, par un 500 « Erreur interne » qui ne nomme rien. Le refus
       * doit venir d'ici, où il peut dire QUOI.
       */
      if (natureDe(s.figure) !== 'surface') {
        /* Le bouton n'existe pas pour ces natures — mais un geste au clavier
           ou un appel direct doit rencontrer le même refus, et le même motif. */
        setEtat((p) => ({
          ...p,
          erreur: 'Seule une emprise surfacique se dépose au registre des parcelles.',
        }));
        return;
      }
      if (!estDeposable(s.figure)) {
        setEtat((p) => ({ ...p, erreur: 'La figure porte une anomalie bloquante : le dépôt est refusé.' }));
        return;
      }
      /**
       * ⚠️⚠️ CORRIGER N'EST PAS CRÉER — ET CE BLOC EXISTE PARCE QUE LES DEUX
       * FAISAIENT LA MÊME CHOSE. Mesuré le 23/09/2026 : « Modifier GN-PAR-… »
       * rouvrait bien la géométrie de cette parcelle, et le dépôt appelait
       * `POST /parcelles` dans TOUS les cas. Le geste produisait une SECONDE
       * parcelle en projet, laissait l'originale intacte, et ne rattachait ni
       * l'une ni l'autre — sans une erreur. Le bouton nommait la parcelle
       * qu'il ne modifiait pas.
       */
      const corr = correction.current;
      if (corr && corr.corps > 1) {
        setEtat((p) => ({
          ...p,
          erreur: `Parcelle ${corr.identifiant} : ${corr.corps} corps séparés, l'éditeur n'en `
            + `tient qu'un. La corriger d'ici effacerait les ${corr.corps - 1} autres.`,
        }));
        return;
      }
      setEtat((p) => ({ ...p, depotEnCours: true, erreur: null }));
      try {
        const geometrie = versGeoJSON(s.figure);
        /* `divisionId` et `numeroCadastral` n'ont pas cours ici : une
           correction déplace un contour, elle ne rattache ni ne renumérote.
           ⚠️ zod SUPPRIME les clés qu'il ne connaît pas — les envoyer quand
           même serait ignoré EN SILENCE, donc le type les retire. */
        const { divisionId: _d, numeroCadastral: _n, ...qualite } = complements;
        const depose = corr
          ? await (deposerCorrection ?? correctionDirecte)(corr.identifiant, { ...qualite, geometrie })
          : await api.parcelles.creer({ ...complements, geometrie })
              .then((r) => {
                const par = (r?.parcelle ?? {}) as Record<string, unknown>;
                return {
                  identifiant: String(par.identifiant ?? par.id ?? '—'),
                  statut: String(par.statut ?? 'projet'),
                  acte: 'creation' as const, ecartSurfacePct: null,
                };
              });
        setEtat((p) => ({ ...p, depotEnCours: false, erreur: null, depose, corrige: null }));
        session.current = null;
        mode.current = 'inactif';
        correction.current = null;
        publier();
        onDepose?.();
      } catch (e) {
        setEtat((p) => ({
          ...p, depotEnCours: false,
          erreur: e instanceof ErreurApi ? e.message : "Le dépôt n'a pas abouti.",
        }));
      }
    },
  };

  gestesRef.current = gestes;

  useEffect(() => () => { if (minuterie.current) clearTimeout(minuterie.current); }, []);

  return [etat, gestes];
}
